import { useEffect } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckSquare, Download, FileText, Lightbulb, PlayCircle, Workflow } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { ErrorState, Loading, PageHeader, Panel } from "../components/common";
import { AuthImg, FlowDiagram } from "../components/HelpParts";
import { downloadFile } from "../components/PaymentLog";
import { replayTour } from "../components/QuickTour";
import { Button } from "../components/ui/button";
import { WelcomePackButtons } from "../components/WelcomePack";

function Guide({ rk, g, n }) {
  return (
    <section id={`${rk}-${g.id}`} className="scroll-mt-24 rounded-2xl border bg-card p-5 md:p-7 animate-in fade-in" data-testid={`guide-${g.id}`}>
      <div className="eyebrow">Guide {n}</div>
      <h2 className="mt-1 font-heading text-base font-bold md:text-lg">{g.title}</h2>
      <div className={`mt-4 grid gap-6 ${g.shot.mobile ? "md:grid-cols-[1fr_240px]" : "lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]"}`}>
        <ol className="space-y-3">{g.steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span><span className="pt-0.5">{s}</span></li>))}</ol>
        {g.screenshot && <AuthImg name={g.screenshot} alt={g.title} className="w-full rounded-lg border shadow-sm" testid={`guide-shot-${g.id}`} />}
      </div>
      <div className="mt-5 rounded-lg border-l-4 border-primary bg-muted/60 px-4 py-3 text-sm" data-testid={`guide-next-${g.id}`}><b>What happens next: </b>{g.next}</div>
      {g.tips.map((t, i) => <p key={i} className="mt-2 flex gap-2 text-sm text-muted-foreground"><Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{t}</p>)}
    </section>
  );
}

function Rollout({ r }) {
  return (
    <section id="admin-rollout" className="scroll-mt-24 space-y-4" data-testid="rollout-checklist">
      <Panel eyebrow="For the go-live" title="Rollout & go-live checklist">
        <ul className="space-y-3">{r.checklist.map(([t, d], i) => (
          <li key={i} className="flex gap-3 text-sm" data-testid={`rollout-item-${i}`}><CheckSquare className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span><b>{t}</b><br /><span className="text-muted-foreground">{d}</span></span></li>))}</ul>
      </Panel>
      <Panel eyebrow="Suggested plan" title="Week-by-week rollout">
        <ol className="relative space-y-4 border-l-2 border-primary/40 pl-5">{r.plan.map(([t, d], i) => (
          <li key={i} className="text-sm" data-testid={`rollout-week-${i}`}><span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-primary" /><b>{t}</b><br /><span className="text-muted-foreground">{d}</span></li>))}</ol>
      </Panel>
    </section>
  );
}

function RolePage({ rk }) {
  const { data, error, status } = useApi(`/help/roles/${rk}`, { live: false });
  const { hash } = useLocation();
  useEffect(() => { if (data && hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150); }, [data, hash]);
  if (error) return <ErrorState message={error} status={status} />;
  if (!data) return <Loading />;
  return (
    <div className="space-y-6" data-testid={`help-role-${rk}`}>
      <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
        <Panel eyebrow="What you do" title={data.who}>
          <p className="text-sm text-muted-foreground">{data.intro}</p>
          <ul className="mt-4 space-y-2">{data.what.map((w, i) => <li key={i} className="flex gap-2 text-sm"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />{w}</li>)}</ul>
        </Panel>
        <Panel eyebrow="Your routine" title="Day to day and each quarter" testid="help-routine">
          {Object.entries(data.routine).map(([p, items]) => (
            <div key={p} className="mb-3"><div className="text-sm font-semibold">{p}</div><ol className="ml-4 list-decimal text-sm text-muted-foreground">{items.map((x, i) => <li key={i}>{x}</li>)}</ol></div>))}
        </Panel>
      </div>
      <nav className="flex flex-wrap gap-2" data-testid="help-guide-index">{data.guides.map((g, i) => (
        <a key={g.id} href={`#${rk}-${g.id}`} className="rounded-full border px-3 py-1 text-xs font-semibold transition-colors hover:border-primary hover:text-primary">{i + 1}. {g.title}</a>))}
        {data.rollout && <a href="#admin-rollout" className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground" data-testid="help-rollout-link">Rollout & go-live checklist</a>}</nav>
      {data.guides.map((g, i) => <Guide key={g.id} rk={rk} g={g} n={i + 1} />)}
      {data.rollout && <Rollout r={data.rollout} />}
      <Panel eyebrow="FAQs" title="Common questions" testid="help-faqs">
        <dl className="space-y-4">{data.faqs.map(([q, a], i) => <div key={i}><dt className="text-sm font-semibold">{q}</dt><dd className="text-sm text-muted-foreground">{a}</dd></div>)}</dl>
      </Panel>
    </div>
  );
}

function Flows() {
  const { data } = useApi("/help/flows", { live: false });
  if (!data) return <Loading />;
  return <div className="space-y-8" data-testid="help-flows">{data.flows.map((f, i) => (
    <section key={f.id} id={`flow-${f.id}`} className="scroll-mt-24"><h2 className="mb-3 font-heading text-base font-bold md:text-lg">Flow {i + 1} · {f.title}</h2><FlowDiagram flow={f} lanes={data.lanes} testid={`flow-${f.id}`} /></section>))}
    <p className="text-xs text-muted-foreground">Click a step to open the guide for the person who does it.</p></div>;
}

function Glossary() {
  const { data } = useApi("/help/glossary", { live: false });
  if (!data) return <Loading />;
  return <Panel eyebrow="Plain English" title="Glossary" testid="help-glossary"><dl className="grid gap-4 md:grid-cols-2">{data.map((g) => <div key={g.term}><dt className="text-sm font-semibold">{g.term}</dt><dd className="text-sm text-muted-foreground">{g.text}</dd></div>)}</dl></Panel>;
}

const tabCls = ({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${isActive ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted"}`;

export default function Help() {
  const { section } = useParams();
  const { isPartner } = useAuth();
  const navigate = useNavigate();
  const { data } = useApi("/help", { live: false });
  useEffect(() => { if (data && !section) navigate(`/help/${data.home}`, { replace: true }); }, [data, section, navigate]);
  if (!data || !section) return <Loading />;
  const role = data.roles.find((r) => r.key === section);
  const full = () => downloadFile("/help/guide.pdf", "EPIL JV Hub user guide.pdf");
  return (
    <div className="space-y-6" data-testid="help-page">
      <PageHeader eyebrow="Help" title="How it works" subtitle={isPartner ? "Your JV Partner guide." : "Step-by-step guides for every role, the process flows and a glossary."}
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={replayTour} data-testid="help-replay-tour"><PlayCircle className="mr-1 h-4 w-4" />Quick tour</Button>
          {role && <Button variant="outline" onClick={() => downloadFile(`/help/roles/${section}/guide.pdf`, "guide.pdf")} data-testid="help-download-role-pdf"><Download className="mr-1 h-4 w-4" />Download PDF</Button>}
          {isPartner && <WelcomePackButtons />}
          <Button variant="outline" onClick={() => downloadFile("/help/how-to-read-your-report.pdf", "How to read your report.pdf")} data-testid="help-how-to-read-pdf"><Download className="mr-1 h-4 w-4" />How to read your report</Button>
          <Button onClick={full} data-testid="help-download-full-pdf"><FileText className="mr-1 h-4 w-4" />{isPartner ? "Download Partner guide" : "Download full user guide"}</Button></div>} />
      <nav className="flex gap-1 overflow-x-auto pb-1 grid-scroll" data-testid="help-tabs">
        {data.roles.map((r) => <NavLink key={r.key} to={`/help/${r.key}`} className={tabCls} data-testid={`help-tab-${r.key}`}>{r.title}{r.key === data.home && <span className="ml-1 text-[10px] font-medium opacity-70">· you</span>}</NavLink>)}
        {!isPartner && <NavLink to="/help/flows" className={tabCls} data-testid="help-tab-flows"><Workflow className="mr-1 inline h-4 w-4" />Process flows</NavLink>}
        <NavLink to="/help/glossary" className={tabCls} data-testid="help-tab-glossary">Glossary</NavLink>
      </nav>
      {section === "flows" && !isPartner ? <Flows /> : section === "glossary" ? <Glossary /> : role ? <RolePage key={section} rk={section} /> : <ErrorState message="You can only see the Partner guide" status={403} />}
    </div>
  );
}
